import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowLeft, faXmark } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import {
  addDays,
  addMinutes,
  differenceInCalendarDays,
  format,
  isSameDay,
  parseISO,
} from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { useMutation } from 'urql';
import { useCalendarContext } from '~/components/layout/CalendarLayout';
import DraggableJobWrapper from '~/components/PlannerView/DraggableJobWrapper';
import { RescheduleJobDialog } from '~/components/PlannerView/RescheduleJobDialog';
import { ScheduleEventPill } from '~/components/PlannerView/ScheduleEventPill';
import { RescheduleDetails } from '~/components/PlannerView/types';
import { graphql } from '~/gql';
import {
  JobStatus,
  RescheduleJobDocument,
  RescheduleJobsInput,
} from '~/gql/graphql';
import { formatTime } from '~/helpers/formatDate';
import { useAuth } from '~/hooks/useAuth';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { IconButton } from '../ui/nucleus/Button';

// Normalize strict flags that may arrive as boolean, number, or string
const strictFlagIsTrue = (value: unknown): boolean => {
  return value === true || value === '1' || value === 1 || value === 'true';
};

// Use the exact same compiled mutation as BulkReschedule to satisfy typed graphql mapping
const RescheduleJobs_Mutation = graphql(`
  mutation RescheduleJobs($input: RescheduleJobsInput!) {
    rescheduleJobs(input: $input) {
      id
      status
      scheduleStart
      scheduleEnd
      scheduledStartStrict
      scheduledStartDate
      scheduledStartTime
      scheduledEndStrict
      scheduledEndDate
      scheduledEndTime
      completedAt
      permissions
    }
  }
`);

// Types
type Job = {
  id: string;
  summary: string;
  status: string;
  start: string;
  end?: string;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
  scheduledStartStrict?: boolean;
  scheduledEndStrict?: boolean;
  scheduledStartDate?: string | null;
  scheduledEndDate?: string | null;
  timeZone?: string | null;
  assignee?: {
    id: string;
    name: string;
    image?: string | null;
  } | null;
  attributes?: Array<{
    id: string;
    type: string;
    category?: string | null;
    name: string;
    value: string[];
  }> | null;
  filtered?: boolean;
};

type CalendarJobFromContext = {
  id: string;
  summary: string;
  status: string;
  start: string;
  end?: string | null;
  scheduledStartTime?: string | null;
  scheduledEndTime?: string | null;
  scheduledStartStrict?: boolean;
  scheduledEndStrict?: boolean;
  scheduledStartDate?: string | null;
  scheduledEndDate?: string | null;
  timeZone?: string | null;
  assignee?: { id: string; name: string; image?: string | null } | null;
  attributes?: Array<{
    id: string;
    type: string;
    category?: string | null;
    name: string;
    value: string[];
  }> | null;
  filtered?: boolean;
};

type PlaceData = {
  id: string;
  name: string;
  calendar: CalendarJobFromContext[];
};

// Time configuration
const TIME_CONFIG = {
  startHour: 6, // 6 AM (legacy; day view will derive dynamic range)
  endHour: 23, // 11 PM (legacy; day view will derive dynamic range)
  hourHeight: 60, // pixels per hour
  snapToMinutes: 30, // drag snap sensitivity
  clickSensitivityMinutes: 60, // click area sensitivity
};

// Default visible window for day view; will expand if jobs fall outside
const DAY_DEFAULT_START_HOUR = 6; // 6 AM
const DAY_DEFAULT_END_HOUR = 20; // 8 PM

// Generate time slots for the grid
const generateTimeSlots = (startHour: number, endHour: number) => {
  const slots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    const time = new Date();
    time.setHours(hour, 0, 0, 0);
    slots.push({
      hour,
      time,
      label: format(time, 'ha'),
      position: (hour - startHour) * TIME_CONFIG.hourHeight,
    });
  }
  return slots;
};

// Build a Date for the given day with an HH:mm time string
// If jobTimeZone is provided, interprets hhmm as being in that timezone and converts to local
// Otherwise, interprets hhmm as local time (backward compatibility)
const buildDateFromHHMM = (
  baseDate: Date,
  hhmm?: string | null,
  jobTimeZone?: string | null
): Date | null => {
  if (!hhmm) return null;
  const [hStr, mStr] = hhmm.split(':');
  const hours = Number(hStr);
  const minutes = Number(mStr ?? '0');
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  if (jobTimeZone) {
    // Interpret hhmm as being in the job's timezone.
    // Anchor the date in the job's timezone to avoid day skew: YYYY-MM-DDTHH:mm
    const dateStr = formatInTimeZone(baseDate, jobTimeZone, 'yyyy-MM-dd');
    const dateTimeString = `${dateStr}T${hhmm}`;

    // Convert from job timezone to UTC, then to local timezone
    const utcDate = zonedTimeToUtc(dateTimeString, jobTimeZone);
    return utcDate;
  }

  // Fallback: interpret as local time (backward compatibility)
  const d = new Date(baseDate);
  d.setHours(hours, minutes, 0, 0);
  return d;
};

// Time Grid Component with integrated drop zones
type DragItem = {
  eventId?: string;
  status?: string;
  siteId?: string;
  scheduledStart?: string | Date;
  jobPreview?: React.ReactNode;
  jobName?: string;
  assigneeName?: string;
  assigneeId?: string;
};

type TimeGridProps = {
  onTimeSlotClick: (hour: number, minutes: number) => void;
  onTimeSlotDrop: (hour: number, minutes: number, dragItem: DragItem) => void;
  selectedDate: Date;
  siteId: string;
  startHour: number;
  endHour: number;
  isDraggingAny?: boolean;
  children: React.ReactNode;
  computePreviewPlacement?: (
    hour: number,
    minutes: number,
    lane?: number
  ) => { leftPercent: number; widthPercent: number } | null;
  onGlobalHoverStart?: (hour: number, minutes: number, lane?: number) => void;
  onGlobalHoverEnd?: () => void;
};

const TimeGrid: React.FC<TimeGridProps> = ({
  onTimeSlotClick,
  onTimeSlotDrop,
  selectedDate,
  siteId,
  startHour,
  endHour,
  children,
  computePreviewPlacement,
  onGlobalHoverStart,
  onGlobalHoverEnd,
}) => {
  const timeSlots = generateTimeSlots(startHour, endHour);
  const totalHeight = (endHour - startHour + 1) * TIME_CONFIG.hourHeight;
  const [hoveredSlot, setHoveredSlot] = useState<{
    hour: number;
    minutes: number;
    lane?: number;
  } | null>(null);

  // Hover throttling is handled in parent and passed via onGlobalHoverStart/End

  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleGlobalDragOver = (e: React.DragEvent) => {
    if (!containerRef.current) return;
    if (!onGlobalHoverStart) return;
    // Always prevent default so the browser considers this a drop target
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const y = Math.max(0, e.clientY - rect.top);
    const minutesFromTop = (y / TIME_CONFIG.hourHeight) * 60;
    const hourFloat = minutesFromTop / 60 + startHour;
    let hour = Math.floor(hourFloat);
    if (hour < startHour) hour = startHour;
    if (hour > endHour) hour = endHour;
    const withinHourMins = Math.max(0, Math.min(59.999, minutesFromTop % 60));
    const minutes = withinHourMins < 30 ? 0 : 30;
    const lane = Math.min(2, Math.floor((x / rect.width) * 3));
    onGlobalHoverStart(hour, minutes, lane);
  };

  const handleGlobalDragLeave = () => {
    onGlobalHoverEnd?.();
  };

  return (
    <div
      ref={containerRef}
      className='relative z-0'
      style={{ height: totalHeight }}
      onDragOver={handleGlobalDragOver}
      onDragLeave={handleGlobalDragLeave}
    >
      {/* Time labels and grid lines with drop zones */}
      {timeSlots.map(({ hour, label, position }) => (
        <div
          key={hour}
          className='absolute left-0 right-0 border-t border-gray-200 first:border-t-0'
          style={{ top: position }}
        >
          {/* Half-hour faint line */}
          <div
            className='absolute left-0 right-0 border-t border-gray-100'
            style={{ top: TIME_CONFIG.hourHeight / 2 }}
          />
          {/* Time label */}
          <div className='absolute -left-12 -top-2 w-10 text-right text-xs text-gray-500'>
            {label}
          </div>

          {/* Drop zone time slots split into half-hours */}
          <div className='relative w-full'>
            <TimeSlotDropZone
              hour={hour}
              minutes={0}
              height={TIME_CONFIG.hourHeight / 2}
              selectedDate={selectedDate}
              siteId={siteId}
              onDrop={(h, m, item) => onTimeSlotDrop(h, m, item)}
              onTimeSlotClick={onTimeSlotClick}
              onHoverStart={(h, m, lane) => {
                setHoveredSlot({ hour: h, minutes: m, lane });
                onGlobalHoverStart?.(h, m, lane);
              }}
              onHoverEnd={() => {
                setHoveredSlot(null);
                onGlobalHoverEnd?.();
              }}
              computePreviewPlacement={computePreviewPlacement}
            >
              <div
                className='w-full'
                style={{ height: TIME_CONFIG.hourHeight / 2 }}
              />
            </TimeSlotDropZone>
            <TimeSlotDropZone
              hour={hour}
              minutes={30}
              height={TIME_CONFIG.hourHeight / 2}
              selectedDate={selectedDate}
              siteId={siteId}
              onDrop={(h, m, item) => onTimeSlotDrop(h, m, item)}
              onTimeSlotClick={onTimeSlotClick}
              onHoverStart={(h, m, lane) => {
                setHoveredSlot({ hour: h, minutes: m, lane });
                onGlobalHoverStart?.(h, m, lane);
              }}
              onHoverEnd={() => {
                setHoveredSlot(null);
                onGlobalHoverEnd?.();
              }}
              computePreviewPlacement={computePreviewPlacement}
            >
              <div
                className='w-full'
                style={{ height: TIME_CONFIG.hourHeight / 2 }}
              />
            </TimeSlotDropZone>
          </div>
        </div>
      ))}

      {/* Job pills container - sits above but does not block slot hover/click. Individual pills enable pointer events themselves. */}
      <div
        className='absolute inset-0 z-[100] pl-2 pr-2'
        style={{ pointerEvents: 'none' }}
        data-daygrid={'1'}
      >
        {children}
      </div>

      {/* Always-on-top hover highlight to avoid being occluded by pills */}
      {hoveredSlot && (
        <div
          className='bg-blue-100/50 active:bg-blue-200/50 pointer-events-none absolute left-0 right-0 z-[50]'
          style={{
            top:
              (hoveredSlot.hour - startHour) * TIME_CONFIG.hourHeight +
              (hoveredSlot.minutes === 30 ? TIME_CONFIG.hourHeight / 2 : 0),
            height: TIME_CONFIG.hourHeight / 2,
          }}
        >
          <div
            className='bg-blue-100/50 active:bg-blue-200/50 mx-0 h-full w-full rounded-none'
            style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)', // blue-500 @ ~28%
            }}
          />
        </div>
      )}
    </div>
  );
};

// Calculate position for a job based on its scheduled time and clamp within visible day range
// Also expose the clamped start/end minutes relative to the day's visible start for overlap math
const calculateJobPosition = (
  job: Job,
  dayStartHour: number,
  dayEndHour: number,
  selectedDate: Date
) => {
  let startTime: Date | null = null;
  let endTime: Date | null = null;

  if (job.scheduledStartTime) {
    startTime = buildDateFromHHMM(
      selectedDate,
      job.scheduledStartTime,
      job.timeZone
    );
    if (!startTime) {
      return {
        top: 0,
        height: TIME_CONFIG.hourHeight,
        startMin: 0,
        endMin: 60,
      };
    }
    endTime = job.scheduledEndTime
      ? buildDateFromHHMM(selectedDate, job.scheduledEndTime, job.timeZone)
      : addMinutes(startTime, 120); // default 120 minutes when only start present
  } else if (job.scheduledEndTime) {
    // Derive a one-hour window ending at scheduledEndTime when start is missing
    endTime = buildDateFromHHMM(
      selectedDate,
      job.scheduledEndTime,
      job.timeZone
    );
    if (!endTime) {
      return {
        top: 0,
        height: TIME_CONFIG.hourHeight,
        startMin: 0,
        endMin: 60,
      };
    }
    startTime = addMinutes(endTime, -60);
  } else {
    // No scheduled times available
    return { top: 0, height: TIME_CONFIG.hourHeight, startMin: 0, endMin: 60 };
  }

  // At this point startTime and endTime are guaranteed non-null
  const toMinutes = (d: Date) => d.getHours() * 60 + d.getMinutes();
  const startOfDayMinutes = dayStartHour * 60;
  const endOfDayMinutes = (dayEndHour + 1) * 60; // include last hour fully

  const startMinutes = toMinutes(startTime as Date);
  let endMinutes = toMinutes(endTime as Date);

  // If the event crosses midnight (end < start), show until end of day
  if ((endTime as Date).getTime() <= (startTime as Date).getTime()) {
    endMinutes = endOfDayMinutes;
  }

  // Clamp to visible range
  const clampedStart = Math.max(
    startOfDayMinutes,
    Math.min(startMinutes, endOfDayMinutes - 1)
  );
  const clampedEnd = Math.max(
    clampedStart + 1,
    Math.min(endMinutes, endOfDayMinutes)
  );

  const topPx =
    ((clampedStart - startOfDayMinutes) / 60) * TIME_CONFIG.hourHeight;
  const heightPx = Math.max(
    30,
    ((clampedEnd - clampedStart) / 60) * TIME_CONFIG.hourHeight
  );

  return {
    top: topPx,
    height: heightPx,
    startMin: clampedStart - startOfDayMinutes,
    endMin: clampedEnd - startOfDayMinutes,
  };
};

// Using shared DraggableJobWrapper from PlannerView

// Helper functions for onboarding event dispatching
const dispatchOnboardingDragStart = (jobId: string) => {
  try {
    window.dispatchEvent(
      new CustomEvent('pf:onboarding_job_dragstart', {
        detail: { jobId },
      })
    );
  } catch (err) {
    console.error('Failed to dispatch onboarding_job_dragstart:', err);
  }
};

const dispatchOnboardingDragEnd = (jobId: string) => {
  try {
    window.dispatchEvent(
      new CustomEvent('pf:onboarding_job_dragend', {
        detail: { jobId },
      })
    );
  } catch (err) {
    console.error('Failed to dispatch onboarding_job_dragend:', err);
  }
};

// Drop Zone for time slots
const TimeSlotDropZone: React.FC<{
  hour: number;
  minutes?: number; // 0 or 30 for half-hour sensitivity
  height?: number; // optional height override for half slot
  selectedDate: Date;
  siteId: string;
  onDrop: (hour: number, minutes: number, dragItem: DragItem) => void;
  onTimeSlotClick: (hour: number, minutes: number) => void;
  children: React.ReactNode;
  onHoverStart?: (hour: number, minutes: number, lane?: number) => void;
  onHoverEnd?: () => void;
  computePreviewPlacement?: (
    hour: number,
    minutes: number,
    lane?: number
  ) => { leftPercent: number; widthPercent: number } | null;
}> = ({
  hour,
  minutes,
  height,
  selectedDate,
  onDrop,
  onTimeSlotClick,
  children,
  onHoverStart,
  onHoverEnd,
  computePreviewPlacement,
}) => {
  // Guard to avoid double firing from touchend followed by synthetic click
  const lastTouchAtRef = useRef<number>(0);
  const triggerCreateAtSlot = useCallback(() => {
    const mins = minutes ?? 0;
    onTimeSlotClick(hour, mins);
  }, [hour, minutes, onTimeSlotClick]);
  const lastDropAtRef = useRef<number>(0);
  // Track touch movement to distinguish taps from scrolls
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef<boolean>(false);
  const TOUCH_MOVE_THRESHOLD = 10; // pixels - if touch moves more than this, consider it a scroll
  const [{ isOver, canDrop, item }, drop] = useDrop({
    accept: 'job',
    drop: (item: any) => {
      onDrop(hour, minutes ?? 0, item);
      // Clear hover overlay after drop
      onHoverEnd?.();
      // Record drop timestamp to suppress accidental clicks/taps immediately after drop
      lastDropAtRef.current = Date.now();
    },
    collect: (monitor) => {
      const over = monitor.isOver();
      const can = monitor.canDrop();
      const item = monitor.getItem() as any;

      return {
        isOver: over,
        canDrop: can,
        item,
      };
    },
  });

  const isActive = isOver && canDrop;

  return (
    <div
      ref={drop}
      className={classNames(
        'relative w-full cursor-pointer transition-colors',
        isActive
          ? 'bg-blue-200/30'
          : 'hover:bg-blue-100/30 active:bg-blue-200/30'
      )}
      style={{ height: height ?? TIME_CONFIG.hourHeight }}
      role='button'
      tabIndex={0}
      aria-label={`Create job at ${format(
        new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          hour,
          minutes ?? 0
        ),
        'h:mm a'
      )}`}
      onDragOver={(e) => {
        // Firefox requires preventing default on dragover to allow drop
        e.preventDefault();
        try {
          if ((e as unknown as DragEvent).dataTransfer) {
            (e as unknown as DragEvent).dataTransfer!.dropEffect = 'move';
          }
        } catch (err) {
          console.error('DayView: failed to set drag dropEffect:', err);
        }
        // Also propagate hover to global handler so pills compress even if slot is covered by a pill
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const relX = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
        const lane = Math.min(2, Math.floor((relX / rect.width) * 3));
        onHoverStart?.(hour, minutes ?? 0, lane);
      }}
      onClick={() => {
        const now = Date.now();
        if (now - (lastDropAtRef.current || 0) < 350) {
          return;
        }
        if (now - (lastTouchAtRef.current || 0) < 500) {
          // Skip click fired immediately after a touch gesture
          return;
        }
        triggerCreateAtSlot();
      }}
      onPointerDown={(e) => {
        // Initialize touch tracking for pointer events (touch/pen)
        if (e.pointerType === 'touch' || e.pointerType === 'pen') {
          touchStartPosRef.current = { x: e.clientX, y: e.clientY };
          hasMovedRef.current = false;
        }
      }}
      onPointerMove={(e) => {
        // Check if pointer has moved significantly (scrolling)
        if (
          (e.pointerType === 'touch' || e.pointerType === 'pen') &&
          touchStartPosRef.current
        ) {
          const dx = Math.abs(e.clientX - touchStartPosRef.current.x);
          const dy = Math.abs(e.clientY - touchStartPosRef.current.y);
          const totalMove = Math.sqrt(dx * dx + dy * dy);
          if (totalMove > TOUCH_MOVE_THRESHOLD) {
            hasMovedRef.current = true;
          }
        }
      }}
      onPointerUp={(e) => {
        const now = Date.now();
        if (now - (lastDropAtRef.current || 0) < 350) {
          if (e.pointerType === 'touch' || e.pointerType === 'pen') {
            touchStartPosRef.current = null;
            hasMovedRef.current = false;
          }
          return;
        }
        if (e.pointerType === 'touch' || e.pointerType === 'pen') {
          // If touch moved significantly, this was a scroll - don't trigger job creation
          if (hasMovedRef.current) {
            touchStartPosRef.current = null;
            hasMovedRef.current = false;
            return;
          }
          lastTouchAtRef.current = Date.now();
          touchStartPosRef.current = null;
          hasMovedRef.current = false;
          triggerCreateAtSlot();
        }
      }}
      onTouchStart={(e) => {
        // Track initial touch position to detect scrolling
        const touch = e.touches[0];
        if (touch) {
          touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
          hasMovedRef.current = false;
        }
      }}
      onTouchMove={(e) => {
        // Check if touch has moved significantly (scrolling)
        const touch = e.touches[0];
        if (touch && touchStartPosRef.current) {
          const dx = Math.abs(touch.clientX - touchStartPosRef.current.x);
          const dy = Math.abs(touch.clientY - touchStartPosRef.current.y);
          const totalMove = Math.sqrt(dx * dx + dy * dy);
          if (totalMove > TOUCH_MOVE_THRESHOLD) {
            hasMovedRef.current = true;
          }
        }
      }}
      onTouchEnd={() => {
        const now2 = Date.now();
        if (now2 - (lastDropAtRef.current || 0) < 350) {
          touchStartPosRef.current = null;
          hasMovedRef.current = false;
          return;
        }
        // If touch moved significantly, this was a scroll - don't trigger job creation
        if (hasMovedRef.current) {
          touchStartPosRef.current = null;
          hasMovedRef.current = false;
          return;
        }
        // Fallback for environments where pointer events may not fire
        const now = Date.now();
        if (now - (lastTouchAtRef.current || 0) < 50) {
          touchStartPosRef.current = null;
          hasMovedRef.current = false;
          return;
        }
        lastTouchAtRef.current = now;
        touchStartPosRef.current = null;
        hasMovedRef.current = false;
        triggerCreateAtSlot();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const mins = minutes ?? 0;
          onTimeSlotClick(hour, mins);
        }
      }}
      onMouseEnter={() => {
        const lane = 0;
        onHoverStart?.(hour, minutes ?? 0, lane);
      }}
      onMouseLeave={() => onHoverEnd?.()}
      onDragLeave={() => onHoverEnd?.()}
    >
      {children}
      {isActive && (
        <>
          {!item?.jobPreview && (
            <div className='bg-blue-100/50 pointer-events-none absolute inset-0 z-[210]' />
          )}
          {item?.jobPreview &&
            (() => {
              const placement = computePreviewPlacement?.(
                hour,
                minutes ?? 0,
                undefined
              );
              const style: React.CSSProperties = placement
                ? {
                    position: 'absolute',
                    top: 4,
                    left: `calc(${placement.leftPercent}% + 2px)`,
                    width: `calc(${placement.widthPercent}% - 4px)`,
                  }
                : { position: 'absolute', top: 4, left: 8, right: 8 };
              return (
                <div
                  className='pointer-events-none absolute z-[210]'
                  style={style}
                >
                  {item.jobPreview}
                </div>
              );
            })()}
        </>
      )}
    </div>
  );
};

// Job Pill Component with positioning and drag capability
const DayJobPill: React.FC<{
  job: Job;
  siteId: string;
  onClick: () => void;
  startHour: number;
  endHour: number;
  selectedDate: Date;
  onDragBegin?: () => void;
  onDragEnd?: () => void;
  /** Optional absolute horizontal placement for overlapping layout */
  leftPercent?: number;
  widthPercent?: number;
}> = ({
  job,
  siteId,
  onClick,
  startHour,
  endHour,
  selectedDate,
  onDragBegin,
  onDragEnd,
  leftPercent,
  widthPercent,
}) => {
  const position = calculateJobPosition(job, startHour, endHour, selectedDate);

  // Detect whether this job is one of the onboarding jobs (stored in localStorage by onboarding flow)
  const isOnboardingJob = (() => {
    try {
      const raw = window.localStorage.getItem('onboardingJobs');
      const ids: string[] = raw ? JSON.parse(raw) : [];
      return Array.isArray(ids) && ids.includes(job.id);
    } catch {
      return false;
    }
  })();
  return (
    <div
      className='pointer-events-auto absolute z-[6] px-1'
      style={{
        top: position.top,
        height: position.height,
        left:
          typeof leftPercent === 'number' ? `calc(${leftPercent}% + 1px)` : 0,
        width:
          typeof widthPercent === 'number'
            ? `calc(${widthPercent}% - 2px)`
            : '100%',
      }}
      data-daypill={'1'}
      data-jobid={job.id}
    >
      <DraggableJobWrapper
        eventDetails={{
          id: job.id,
          status: job.status,
          siteId,
          scheduledStart: new Date(job.start),
          days: 1,
          jobName: job.summary,
          assigneeName: job.assignee?.name || '',
          assigneeId: job.assignee?.id,
        }}
        hideWhileDragging={true}
        preview={
          <ScheduleEventPill
            event={job}
            height={position.height}
            width={'100%'}
            absolutePosition={false}
            showDates={false}
            selected={false}
            inDialog={false}
            filtered={false}
          />
        }
        onDragBegin={() => {
          onDragBegin?.();
          if (isOnboardingJob) {
            dispatchOnboardingDragStart(job.id);
          }
        }}
        onDragEnd={() => {
          onDragEnd?.();
          if (isOnboardingJob) {
            dispatchOnboardingDragEnd(job.id);
          }
        }}
      >
        <div onClick={onClick} className='h-full'>
          <DayScheduledPill
            job={job}
            height={position.height}
            selectedDate={selectedDate}
          />
        </div>
      </DraggableJobWrapper>
    </div>
  );
};

// Dedicated pill for scheduled jobs in DayView to match provided UI
const getScheduledPillClasses = (status?: string): string => {
  const normalized = (status || '').toLowerCase();
  switch (normalized) {
    case 'accepted':
      return 'bg-green-50 border-green-200';
    case 'inprogress':
      return 'bg-indigo-100 border-indigo-200';
    case 'created':
      return 'bg-gray-50 border-gray-200';
    case 'cancelled':
      return 'bg-red-50 border-red-200';
    case 'complete':
      return 'bg-gray-800 border-gray-900';
    default:
      return 'bg-white border-gray-200';
  }
};

const getStatusTextClasses = (status?: string): string => {
  const normalized = (status || '').toLowerCase();
  switch (normalized) {
    case 'accepted':
      return 'text-green-700';
    case 'inprogress':
      return 'text-blue-700';
    case 'created':
      return 'text-gray-700';
    case 'cancelled':
      return 'text-red-800';
    case 'complete':
      return 'text-white';
    default:
      return 'text-gray-700';
  }
};

const DayScheduledPill: React.FC<{
  job: Job;
  height: number | string;
  selectedDate: Date;
}> = ({ job, height }) => {
  const { t } = useTranslation('job');
  const subtext =
    job.attributes?.find((a) => a.name === 'Notes')?.value?.[0] ||
    job.attributes?.find((a) => a.name === 'Tasks')?.value?.[0] ||
    undefined;

  // Show time if explicitly set (including "00:00" as midnight)
  // Use formatTime to display scheduled time as-is (no timezone conversion)
  const hasStartTime = !!job.scheduledStartTime;
  const hasEndTime = !!job.scheduledEndTime;
  const timeLabel = hasStartTime
    ? hasEndTime
      ? `${formatTime(job.scheduledStartTime)} – ${formatTime(
          job.scheduledEndTime
        )}`
      : formatTime(job.scheduledStartTime)
    : hasEndTime
    ? formatTime(job.scheduledEndTime)
    : '';

  return (
    <div
      className={classNames(
        'border-l-3 flex h-full w-full select-none flex-col rounded-lg border px-3 py-2',
        getScheduledPillClasses(job.status)
      )}
      style={{ height }}
    >
      <div className='flex items-start justify-between gap-2'>
        <div className='min-w-0'>
          <div
            className={classNames(
              'truncate text-sm font-semibold leading-5',
              job.status === 'Complete' && 'text-white'
            )}
          >
            {job.summary}
          </div>
          {subtext && (
            <div
              className={classNames(
                'truncate text-[11px] leading-4',
                job.status === 'Complete' ? 'text-gray-50' : 'text-secondary'
              )}
            >
              {subtext}
            </div>
          )}
        </div>
        {job.status && (
          <div
            className={classNames(
              'ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
              getStatusTextClasses(job.status)
            )}
          >
            {t(job.status)}
          </div>
        )}
      </div>
      <div
        className={classNames(
          'mt-auto flex items-center justify-between pt-1 text-[11px] leading-4',
          getStatusTextClasses(job.status)
        )}
      >
        <span
          className={classNames(
            'truncate',
            job.status === 'Complete' ? 'text-gray-50' : 'text-secondary'
          )}
        >
          {timeLabel}
        </span>
      </div>
    </div>
  );
};

// All-day (multi-day) Job Pill - rendered in a compact list above the grid
const AllDayJobPill: React.FC<{
  job: Job;
  siteId: string;
  onClick: () => void;
  onDragBegin?: () => void;
  onDragEnd?: () => void;
}> = ({ job, siteId, onClick, onDragBegin, onDragEnd }) => {
  const isOnboardingJob = (() => {
    try {
      const raw = window.localStorage.getItem('onboardingJobs');
      const ids: string[] = raw ? JSON.parse(raw) : [];
      return Array.isArray(ids) && ids.includes(job.id);
    } catch {
      return false;
    }
  })();
  return (
    <DraggableJobWrapper
      eventDetails={{
        id: job.id,
        status: job.status,
        siteId,
        scheduledStart: new Date(job.start),
        days: 1,
        jobName: job.summary,
        assigneeName: job.assignee?.name || '',
        assigneeId: job.assignee?.id,
      }}
      preview={
        <ScheduleEventPill
          event={job}
          height='24px'
          width='100%'
          absolutePosition={false}
          showDates={false}
          selected={false}
          inDialog={false}
          filtered={job.filtered || false}
        />
      }
      onDragBegin={() => {
        onDragBegin?.();
        if (isOnboardingJob) {
          dispatchOnboardingDragStart(job.id);
        }
      }}
      onDragEnd={() => {
        onDragEnd?.();
        if (isOnboardingJob) {
          dispatchOnboardingDragEnd(job.id);
        }
      }}
    >
      <div
        onClick={onClick}
        className={classNames({
          'cursor-pointer':
            job.status === 'Created' ||
            job.status === 'Offered' ||
            job.status === 'Accepted',
        })}
        data-usetiful={isOnboardingJob ? 'onboarding-job' : undefined}
      >
        <ScheduleEventPill
          event={job}
          height='24px'
          width='100%'
          absolutePosition={false}
          showDates={false}
          selected={false}
          inDialog={false}
          filtered={job.filtered || false}
        />
      </div>
    </DraggableJobWrapper>
  );
};

// Unscheduled Job Pill with drag capability
const UnscheduledJobPill: React.FC<{
  job: Job;
  siteId: string;
  onClick: () => void;
  onDragBegin?: () => void;
  onDragEnd?: () => void;
}> = ({ job, siteId, onClick, onDragBegin, onDragEnd }) => {
  const isOnboardingJob = (() => {
    try {
      const raw = window.localStorage.getItem('onboardingJobs');
      const ids: string[] = raw ? JSON.parse(raw) : [];
      return Array.isArray(ids) && ids.includes(job.id);
    } catch {
      return false;
    }
  })();
  return (
    <DraggableJobWrapper
      eventDetails={{
        id: job.id,
        status: job.status,
        siteId,
        scheduledStart: new Date(job.start),
        days: 1,
        jobName: job.summary,
        assigneeName: job.assignee?.name || '',
        assigneeId: job.assignee?.id,
      }}
      preview={
        <ScheduleEventPill
          event={job}
          height='48px'
          width='100%'
          absolutePosition={false}
          showDates={false}
          selected={false}
          inDialog={false}
          filtered={job.filtered || false}
        />
      }
      onDragBegin={() => {
        onDragBegin?.();
        if (isOnboardingJob) {
          dispatchOnboardingDragStart(job.id);
        }
      }}
      onDragEnd={() => {
        onDragEnd?.();
        if (isOnboardingJob) {
          dispatchOnboardingDragEnd(job.id);
        }
      }}
    >
      <div
        onClick={onClick}
        className={classNames({
          'cursor-pointer':
            job.status === 'Created' ||
            job.status === 'Offered' ||
            job.status === 'Accepted',
        })}
        data-usetiful={isOnboardingJob ? 'onboarding-job' : undefined}
      >
        <ScheduleEventPill
          event={job}
          height='48px'
          width='100%'
          absolutePosition={false}
          showDates={false}
          selected={false}
          inDialog={false}
          filtered={job.filtered || false}
        />
      </div>
    </DraggableJobWrapper>
  );
};

// Current Time Line Component
const CurrentTimeLine: React.FC<{
  selectedDate: Date;
  startHour: number;
  endHour: number;
}> = ({ selectedDate, startHour, endHour }) => {
  const now = new Date();
  const isToday = isSameDay(now, selectedDate);

  if (!isToday) return null;

  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();

  // Only show if within our time range
  if (currentHour < startHour || currentHour > endHour) {
    return null;
  }

  const position =
    (currentHour - startHour) * TIME_CONFIG.hourHeight +
    (currentMinutes / 60) * TIME_CONFIG.hourHeight;

  return (
    <div
      className='absolute left-0 right-0 z-20 flex items-center'
      style={{ top: position }}
    >
      <div className='-ml-1 h-2 w-2 rounded-full bg-red-500' />
      <div className='h-0.5 flex-1 bg-red-500' />
    </div>
  );
};

// Internal DayView Component (the main implementation)
const DayViewImpl: React.FC = () => {
  const { isMobile } = useBreakpoint();
  const { placeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isCurrentUser } = useAuth();

  // GraphQL mutation for date-shift reschedules (parity with Monthly view)
  const [, rescheduleJob] = useMutation(RescheduleJobDocument);

  // Dialog + reschedule state (parity with Monthly view)
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [rescheduleDetails, setRescheduleDetails] =
    useState<RescheduleDetails>();
  const [isRescheduling, setIsRescheduling] = useState(false);

  // Create typed mutation for same-day time change
  const [, rescheduleJobs] = useMutation(RescheduleJobs_Mutation);

  // Dialog and reschedule state now handled by hook
  const [isDraggingAny, setIsDraggingAny] = useState(false);
  type SameDayInput = Pick<
    RescheduleJobsInput,
    'jobIds' | 'startDate' | 'startTime' | 'endTime' | 'endDate'
  >;
  const [pendingSameDayInput, setPendingSameDayInput] =
    useState<SameDayInput | null>(null);
  // Render-driven hover state: identifies the half-hour slot and lane being hovered
  const [hoverInfo, setHoverInfo] = useState<{
    slotStartMin: number; // minutes since day start
    slotEndMin: number;
    lane?: number; // 0..2
  } | null>(null);

  // Get selected date from URL params or default to today
  const dateParam = searchParams.get('date');
  const selectedDate = useMemo(() => {
    if (dateParam) {
      try {
        // Handle the date format properly - could be YYYY-MM-DD
        const parsed = parseISO(dateParam);
        // Check if the parsed date is valid
        if (isNaN(parsed.getTime())) {
          console.warn('Invalid date parameter:', dateParam);
          return new Date();
        }
        return parsed;
      } catch (e) {
        console.warn('Failed to parse date parameter:', dateParam, e);
        return new Date();
      }
    }
    return new Date();
  }, [dateParam]);

  // Get calendar context - this should have all site data
  const context = useCalendarContext();
  const { data, stableData, isLoading } = context || {};
  // Find the specific place data from stable (unfiltered) data for site properties
  // (Similar approach to CalendarView)
  const stablePlaceData = useMemo(() => {
    if (!stableData?.sites || !placeId) return null;
    const decodedPlaceId = decodeURIComponent(placeId);

    // Try name first, then ID (same as CalendarView)
    let site = stableData.sites.find(
      (site: { id: string; name: string }) => site.name === decodedPlaceId
    );
    if (!site) {
      site = stableData.sites.find(
        (site: { id: string; name: string }) =>
          site.id === decodedPlaceId || site.id === placeId
      );
    }
    return site || null;
  }, [stableData?.sites, placeId]);

  // Find the corresponding filtered site from the `data` context for calendar jobs
  const filteredPlaceData = useMemo(() => {
    if (!data || !placeId) return null;
    const decodedPlaceId = decodeURIComponent(placeId);
    // The `data` from context is the filtered list of sites
    return (
      data.find(
        (site: { id: string; name: string }) =>
          site.name === decodedPlaceId ||
          site.id === decodedPlaceId ||
          site.id === placeId
      ) || null
    );
  }, [data, placeId]);

  // Combine stable data with filtered calendar
  const placeData = useMemo<PlaceData | null>(() => {
    if (!stablePlaceData) return null;
    return {
      ...stablePlaceData,
      // Use the calendar from the filtered data, which has `filtered` property on jobs.
      // If the whole site was filtered out (e.g., by date range), use an empty array.
      calendar: (filteredPlaceData?.calendar || []) as CalendarJobFromContext[],
    };
  }, [stablePlaceData, filteredPlaceData]);

  // Local calendar state for optimistic updates (parity with Monthly view)
  const [localCalendar, setLocalCalendar] = useState<PlaceData['calendar']>(
    () => placeData?.calendar || []
  );
  useEffect(() => {
    // Merge incoming context calendar with local state, preserving any locally-set times
    // to avoid flicker/revert after optimistic updates while the server/cache catches up.
    setLocalCalendar((prev) => {
      const incoming = (placeData?.calendar || []) as PlaceData['calendar'];
      if (!prev || prev.length === 0) return incoming;
      const prevMap = new Map(prev.map((j) => [j.id, j]));
      const merged = incoming.map((j) => {
        const prevJob = prevMap.get(j.id);
        if (!prevJob) return j;
        const mergedJob = { ...j } as CalendarJobFromContext;
        if (!j.scheduledStartTime && prevJob.scheduledStartTime) {
          mergedJob.scheduledStartTime = prevJob.scheduledStartTime;
        }
        if (!j.scheduledEndTime && prevJob.scheduledEndTime) {
          mergedJob.scheduledEndTime = prevJob.scheduledEndTime;
        }
        return mergedJob;
      });
      return merged;
    });
  }, [placeData?.calendar]);

  // Filter jobs for selected date and separate scheduled, all-day (multi-day), and unscheduled
  // Also compute dynamic hours range for the day to avoid hook-order changes
  const {
    scheduledJobs,
    allDayJobs,
    unscheduledJobs,
    dayStartHour,
    dayEndHour,
  } = useMemo(() => {
    if (!localCalendar) {
      return {
        scheduledJobs: [],
        allDayJobs: [],
        unscheduledJobs: [],
        dayStartHour: DAY_DEFAULT_START_HOUR,
        dayEndHour: DAY_DEFAULT_END_HOUR,
      };
    }

    const inRange = (date: Date, start: Date, end: Date) => {
      const from = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate()
      );
      const to = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return d.getTime() >= from.getTime() && d.getTime() <= to.getTime();
    };

    const scheduled: Job[] = [];
    const allDay: Job[] = [];
    const unscheduled: Job[] = [];
    let minHour = DAY_DEFAULT_START_HOUR;
    let maxHour = DAY_DEFAULT_END_HOUR;

    for (const job of localCalendar) {
      if (!job?.start) continue;
      const start = parseISO(job.start);
      const hasEnd = Boolean(job.end);
      const end = hasEnd ? parseISO(job.end as string) : null;
      const isMultiDay = hasEnd && !isSameDay(start, end!);

      // Determine if this job is relevant to the selected date.
      // Prefer tenant-local scheduled*Date when available to avoid viewer TZ skew.
      const selectedKey = format(selectedDate, 'yyyy-MM-dd');
      const isRelevant = isMultiDay
        ? inRange(selectedDate, start, end!)
        : job.scheduledStartDate
        ? job.scheduledStartDate === selectedKey
        : job.scheduledEndDate
        ? job.scheduledEndDate === selectedKey
        : isSameDay(start, selectedDate) ||
          (end ? isSameDay(end, selectedDate) : false);

      if (!isRelevant) continue;

      const derivedStartTime = (() => {
        // Use scheduledStartTime if explicitly set (including "00:00" as midnight)
        if (job.scheduledStartTime) {
          return job.scheduledStartTime;
        }
        // Don't derive from start when scheduledStartTime is null - start is computed
        // and may contain a time even when no explicit time was set
        return null;
      })();
      const derivedEndTime = (() => {
        // Use scheduledEndTime if explicitly set (including "00:00" as midnight)
        if (job.scheduledEndTime) {
          return job.scheduledEndTime;
        }
        // Don't derive from end when scheduledEndTime is null - end is computed
        // and may contain a time even when no explicit time was set
        return null;
      })();

      const mapped: Job = {
        id: job.id,
        summary: job.summary,
        status: job.status,
        start: job.start,
        end: job.end ?? undefined,
        scheduledStartTime: derivedStartTime,
        scheduledEndTime: derivedEndTime,
        scheduledStartStrict: job.scheduledStartStrict,
        scheduledEndStrict: job.scheduledEndStrict,
        scheduledStartDate: job.scheduledStartDate,
        scheduledEndDate: job.scheduledEndDate,
        timeZone: job.timeZone,
        assignee: job.assignee,
        attributes: job.attributes,
        filtered: job.filtered || false,
      };

      if (isMultiDay) {
        allDay.push(mapped);
        continue;
      }

      if (mapped.scheduledStartTime || mapped.scheduledEndTime) {
        // Trust explicit scheduled times from backend regardless of viewer timezone.
        // Treat as scheduled when either start or end time exists.
        scheduled.push(mapped);

        // Track dynamic hour range for this day using selectedDate as the base
        const endCandidate = mapped.scheduledEndTime
          ? buildDateFromHHMM(
              selectedDate,
              mapped.scheduledEndTime,
              mapped.timeZone
            )
          : null;
        const schedStart = mapped.scheduledStartTime
          ? buildDateFromHHMM(
              selectedDate,
              mapped.scheduledStartTime,
              mapped.timeZone
            )
          : endCandidate
          ? addMinutes(endCandidate, -60)
          : null;

        if (schedStart) {
          const s = schedStart;
          const e = endCandidate || addMinutes(schedStart, 120);
          if (e) {
            const sHour = Math.max(
              0,
              Math.min(
                23,
                Math.floor((s.getHours() * 60 + s.getMinutes()) / 60)
              )
            );
            let eMins = e.getHours() * 60 + e.getMinutes();
            if (e.getTime() <= s.getTime()) {
              eMins = 24 * 60;
            }
            const eHour = Math.max(0, Math.min(23, Math.ceil(eMins / 60)));
            minHour = Math.min(minHour, sHour);
            maxHour = Math.max(maxHour, eHour);
          }
        }
      } else {
        unscheduled.push(mapped);
      }
    }

    // Sort scheduled by start time
    scheduled.sort((a, b) => {
      const aDate = buildDateFromHHMM(
        selectedDate,
        a.scheduledStartTime,
        a.timeZone
      );
      const bDate = buildDateFromHHMM(
        selectedDate,
        b.scheduledStartTime,
        b.timeZone
      );
      const aTime = aDate ? aDate.getTime() : 0;
      const bTime = bDate ? bDate.getTime() : 0;
      return aTime - bTime;
    });

    if (maxHour <= minHour) {
      maxHour = Math.min(23, minHour + 1);
    }

    return {
      scheduledJobs: scheduled,
      allDayJobs: allDay,
      unscheduledJobs: unscheduled,
      dayStartHour: minHour,
      dayEndHour: maxHour,
    };
  }, [localCalendar, selectedDate]);

  // Detect if we're in monthly view context by checking current pathname
  const location = useLocation();
  const isMonthlyViewContext = useMemo(() => {
    return location.pathname.startsWith('/jobs/calendar/');
  }, [location.pathname]);

  // Handle job click
  const handleJobClick = useCallback(
    (jobId: string, status?: JobStatus | string) => {
      // Handle bookings separately - they use a different route structure
      if (status === 'Booking') {
        // Navigate to booking view using relative path (same pattern as CalendarView)
        navigate(`bookings/${jobId}`);
        return;
      }

      // Preserve original returnTo when navigating from one job to another
      // This ensures closing only requires one click instead of two
      const currentState = location.state as { returnTo?: string } | null;
      const originalReturnTo = currentState?.returnTo;

      // Helper function to compute returnTo based on context
      const computeReturnTo = () => {
        if (originalReturnTo) return originalReturnTo;

        if (isMonthlyViewContext && placeId) {
          return `/jobs/calendar/${placeId}/day?date=${format(
            selectedDate,
            'yyyy-MM-dd'
          )}`;
        }

        return placeId
          ? `/jobs/planner/${placeId}/day?date=${format(
              selectedDate,
              'yyyy-MM-dd'
            )}`
          : '/jobs/planner';
      };

      const returnTo = computeReturnTo();

      // If the job is a draft (Created), go straight to edit screen
      if (status === JobStatus.Created) {
        if (isMonthlyViewContext && placeId) {
          try {
            sessionStorage.setItem(`returnTo_job_${jobId}`, returnTo);
          } catch {
            /* empty */
          }
          navigate(`/jobs/calendar/${placeId}/${jobId}/edit`, {
            state: { returnTo },
          });
        } else {
          try {
            sessionStorage.setItem(`returnTo_job_${jobId}`, returnTo);
          } catch {
            /* empty */
          }
          navigate(`/jobs/planner/${jobId}/edit`, { state: { returnTo } });
        }
        return;
      }

      // Use absolute paths to avoid route-relative pitfalls
      if (isMonthlyViewContext && placeId) {
        // From monthly calendar day view: /jobs/calendar/:placeId/day -> /jobs/calendar/:placeId/:jobId
        try {
          sessionStorage.setItem(`returnTo_job_${jobId}`, returnTo);
        } catch {
          /* empty */
        }
        navigate(`/jobs/calendar/${placeId}/${jobId}`, {
          state: { returnTo },
        });
        return;
      }
      // From regular planner day view: /jobs/planner/:placeId/day -> /jobs/planner/:jobId
      {
        try {
          sessionStorage.setItem(`returnTo_job_${jobId}`, returnTo);
        } catch {
          /* empty */
        }
        navigate(`/jobs/planner/${jobId}`, { state: { returnTo } });
      }
    },
    [navigate, isMonthlyViewContext, placeId, selectedDate, location.state]
  );

  // Memoized rendering of scheduled job pills with overlap layout
  const renderedScheduledPills = useMemo(() => {
    type LayoutItem = {
      job: Job;
      start: number;
      end: number;
      top: number;
      height: number;
    };

    const items: LayoutItem[] = scheduledJobs.map((job) => {
      const pos = calculateJobPosition(
        job,
        dayStartHour,
        dayEndHour,
        selectedDate
      );
      return {
        job,
        start: pos.startMin,
        end: pos.endMin,
        top: pos.top,
        height: pos.height,
      };
    });

    items.sort((a, b) => a.start - b.start);

    const rendered: React.ReactNode[] = [];
    let clusterStartIdx = 0;
    let clusterMaxEnd = -1;

    const flushCluster = (fromIdx: number, toIdx: number) => {
      if (toIdx < fromIdx) return;
      const cluster = items.slice(fromIdx, toIdx + 1);
      const MAX_COLS = 3;
      const clusterOverlapsHovered =
        isDraggingAny && hoverInfo
          ? cluster.some(
              (e) =>
                e.start < (hoverInfo!.slotEndMin as number) &&
                e.end > (hoverInfo!.slotStartMin as number)
            )
          : false;
      const reservedLane = clusterOverlapsHovered
        ? Math.max(
            0,
            Math.min(
              MAX_COLS - 1,
              typeof hoverInfo?.lane === 'number'
                ? (hoverInfo!.lane as number)
                : 0
            )
          )
        : -1;
      const columnEndTimes: number[] = [];
      const assignedCol: number[] = new Array(cluster.length).fill(0);
      for (let i = 0; i < cluster.length; i++) {
        const ev = cluster[i];
        let placed = false;
        for (let c = 0; c < Math.min(columnEndTimes.length, MAX_COLS); c++) {
          if (c === reservedLane) continue;
          if (ev.start >= columnEndTimes[c]) {
            assignedCol[i] = c;
            columnEndTimes[c] = ev.end;
            placed = true;
            break;
          }
        }
        if (!placed) {
          if (columnEndTimes.length < MAX_COLS) {
            let next = columnEndTimes.length;
            if (next === reservedLane && next + 1 < MAX_COLS) next += 1;
            assignedCol[i] = next;
            columnEndTimes[next] = ev.end;
          } else {
            let idx = 0;
            for (let k = 1; k < MAX_COLS; k++) {
              if (columnEndTimes[k] < columnEndTimes[idx]) idx = k;
            }
            if (idx === reservedLane) idx = (idx + 1) % MAX_COLS;
            assignedCol[i] = idx;
            columnEndTimes[idx] = ev.end;
          }
        }
      }
      const totalCols = Math.max(
        1,
        Math.min(
          MAX_COLS,
          columnEndTimes.length + (reservedLane !== -1 ? 1 : 0)
        )
      );
      const colWidth = 100 / totalCols;
      for (let i = 0; i < cluster.length; i++) {
        const ev = cluster[i];
        const col = assignedCol[i];
        const leftPercent =
          reservedLane !== -1 && col >= reservedLane
            ? (col + 1) * colWidth
            : col * colWidth;
        const widthPercent = colWidth;
        rendered.push(
          <DayJobPill
            key={ev.job.id}
            job={ev.job}
            siteId={placeData!.id}
            onClick={() => handleJobClick(ev.job.id, ev.job.status)}
            startHour={dayStartHour}
            endHour={dayEndHour}
            selectedDate={selectedDate}
            onDragBegin={() => setIsDraggingAny(true)}
            onDragEnd={() => setIsDraggingAny(false)}
            leftPercent={leftPercent}
            widthPercent={widthPercent}
          />
        );
      }
    };

    for (let i = 0; i < items.length; i++) {
      const ev = items[i];
      if (clusterStartIdx === i) {
        clusterMaxEnd = ev.end;
      } else {
        if (ev.start < clusterMaxEnd) {
          clusterMaxEnd = Math.max(clusterMaxEnd, ev.end);
        } else {
          flushCluster(clusterStartIdx, i - 1);
          clusterStartIdx = i;
          clusterMaxEnd = ev.end;
        }
      }
    }
    flushCluster(clusterStartIdx, items.length - 1);
    return rendered;
  }, [
    scheduledJobs,
    hoverInfo,
    dayStartHour,
    dayEndHour,
    selectedDate,
    placeData,
    isDraggingAny,
    handleJobClick,
  ]);

  // Handle time slot click for job creation
  const handleTimeSlotClick = useCallback(
    (hour: number, minutes: number) => {
      const timeString = format(
        new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          hour,
          minutes
        ),
        'HH:mm'
      );

      const params = new URLSearchParams({
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: timeString,
      });

      if (placeData?.id) {
        params.set('siteId', placeData.id);
      } else if (placeId) {
        // Fallback: ensure siteId is included so CreateJobForm can return to DayView
        params.set('siteId', placeId);
      }

      // Route to correct "new" path depending on context
      // - Monthly context expects nested new route under monthly place
      // - Regular (planner) expects absolute /jobs/planner/new
      if (isMonthlyViewContext) {
        navigate(`../new?${params.toString()}`);
      } else {
        navigate(`/jobs/planner/new?${params.toString()}`);
      }
    },
    [navigate, selectedDate, placeData?.id, isMonthlyViewContext, placeId]
  );

  // Optimistic update helpers (parity with Monthly view)
  const revertOptimistic = useCallback((details: RescheduleDetails) => {
    setLocalCalendar((prev) => {
      const next = prev.map((job) => {
        if (job.id !== details.jobId) return job;
        const startDate = new Date(job.start);
        const endDate = job.end ? new Date(job.end) : null;
        const newStart = new Date(
          startDate.getTime() - details.offsetDays * 24 * 60 * 60 * 1000
        ).toISOString();
        const newEnd = endDate
          ? new Date(
              endDate.getTime() - details.offsetDays * 24 * 60 * 60 * 1000
            ).toISOString()
          : undefined;
        return { ...job, start: newStart, end: newEnd };
      });
      return next;
    });
  }, []);

  const applyOptimistic = useCallback((details: RescheduleDetails) => {
    setLocalCalendar((prev) => {
      const next = prev.map((job) => {
        if (job.id !== details.jobId) return job;
        const startDate = new Date(job.start);
        const endDate = job.end ? new Date(job.end) : null;
        const newStart = new Date(
          startDate.getTime() + details.offsetDays * 24 * 60 * 60 * 1000
        ).toISOString();
        const newEnd = endDate
          ? new Date(
              endDate.getTime() + details.offsetDays * 24 * 60 * 60 * 1000
            ).toISOString()
          : undefined;
        return { ...job, start: newStart, end: newEnd };
      });
      return next;
    });
  }, []);

  const handleImmediateReschedule = useCallback(
    (details: RescheduleDetails) => {
      setIsRescheduling(true);
      rescheduleJob({
        input: { id: details.jobId, offsetDays: details.offsetDays },
      })
        .then((result) => {
          if (result.error) {
            console.warn('[DayView] rescheduleJob error, reverting');
            revertOptimistic(details);
          }
        })
        .catch((e) => {
          console.warn('[DayView] rescheduleJob exception', e);
          revertOptimistic(details);
        })
        .finally(() => setIsRescheduling(false));
    },
    [rescheduleJob, revertOptimistic]
  );

  // Reschedule handler (parity with Monthly view)
  const handleReschedule = useCallback(
    (details: RescheduleDetails) => {
      // Optimistic update first
      applyOptimistic(details);

      if (details.jobStatus === JobStatus.Offered) {
        setRescheduleDetails(details);
        setDialogIsOpen(true);
        return;
      }

      if (details.jobStatus === JobStatus.Accepted) {
        if (details.assigneeId && isCurrentUser(details.assigneeId)) {
          handleImmediateReschedule(details);
        } else {
          setRescheduleDetails(details);
          setDialogIsOpen(true);
        }
        return;
      }

      // Created jobs: immediate
      handleImmediateReschedule(details);
    },
    [applyOptimistic, handleImmediateReschedule, isCurrentUser]
  );

  // Handle time slot drop for job rescheduling
  const handleTimeSlotDrop = useCallback(
    (hour: number, minutes: number, dragItem: DragItem) => {
      if (!dragItem?.eventId || !placeData?.id) {
        console.warn('[DayView] drop ignored: missing eventId or placeId', {
          hasEventId: Boolean(dragItem?.eventId),
          hasPlaceId: Boolean(placeData?.id),
        });

        return;
      }

      // Determine original date and whether this job had no scheduled times.
      // If there are no times, we should always treat as a same-day time assignment.
      const originalDate = new Date(dragItem.scheduledStart || selectedDate);
      const prevJob = localCalendar.find((j) => j.id === dragItem.eventId);
      const hasNoTimes =
        !prevJob?.scheduledStartTime && !prevJob?.scheduledEndTime;
      // Normalize dates to yyyy-MM-dd to avoid timezone edge cases when comparing days
      const originalDateKey = format(originalDate, 'yyyy-MM-dd');
      const selectedDateKey = format(selectedDate, 'yyyy-MM-dd');
      const offsetDays = differenceInCalendarDays(selectedDate, originalDate);

      // Map raw status string to GraphQL enum for correct dialog behavior
      const mapStatus = (status?: string): JobStatus | undefined => {
        if (!status) return undefined;
        const normalized = status.toLowerCase();
        if (normalized === 'offered') return JobStatus.Offered;
        if (normalized === 'accepted' || normalized === 'confirmed')
          return JobStatus.Accepted;
        if (normalized === 'created') return JobStatus.Created;
        return undefined;
      };

      const details: RescheduleDetails = {
        jobId: dragItem.eventId,
        jobStatus: mapStatus(dragItem.status),
        jobName: dragItem.jobName,
        assigneeName: dragItem.assigneeName,
        assigneeId: dragItem.assigneeId,
        offsetDays,
        originalDate,
        targetDate: selectedDate,
        originalSiteId: placeData.id,
        targetSiteId: placeData.id,
      };

      // If dropping within the same day, adjust scheduledStartTime to the dropped hour
      const sameDayDrop =
        hasNoTimes || originalDateKey === selectedDateKey || offsetDays === 0;
      if (sameDayDrop) {
        const hh = String(hour).padStart(2, '0');
        const mm = String(minutes).padStart(2, '0');
        const newTime = `${hh}:${mm}`; // Use HH:mm consistently (matches BulkReschedule)

        // Optimistic update: update only existing fields (no creating the missing side)
        const prevTimes = {
          start: prevJob?.scheduledStartTime ?? null,
          end: prevJob?.scheduledEndTime ?? null,
        } as const;

        try {
          let nextEndStr: string | null = null;
          if (prevTimes.start && prevTimes.end) {
            const prevStartDate = buildDateFromHHMM(
              selectedDate,
              prevTimes.start
            );
            const prevEndDate = buildDateFromHHMM(selectedDate, prevTimes.end);
            if (prevStartDate && prevEndDate) {
              const durationMins =
                (prevEndDate.getTime() - prevStartDate.getTime()) / 60000;
              const newStartDate = buildDateFromHHMM(selectedDate, newTime)!;
              const newEndDate = addMinutes(newStartDate, durationMins);
              nextEndStr = format(newEndDate, 'HH:mm');
            }
          }

          setLocalCalendar((prev) =>
            prev.map((job) => {
              if (job.id !== dragItem.eventId) return job;
              const updates: Partial<typeof job> = {};
              if (prevTimes.start || (!prevTimes.start && !prevTimes.end)) {
                updates.scheduledStartTime = newTime;
              }
              if (prevTimes.start && prevTimes.end && nextEndStr) {
                updates.scheduledEndTime = nextEndStr;
              } else if (!prevTimes.start && prevTimes.end) {
                updates.scheduledEndTime = newTime;
              }
              return { ...job, ...updates } as typeof job;
            })
          );
        } catch (err) {
          console.error(
            'DayView: failed to apply optimistic time update:',
            err
          );
        }

        // If job has only end time, derive 1 hour before on backend; otherwise set start time
        const input: SameDayInput = { jobIds: [dragItem.eventId] };
        try {
          const prevStart = prevTimes.start;
          const prevEnd = prevTimes.end;
          if (prevStart && prevEnd) {
            input.startDate = format(selectedDate, 'yyyy-MM-dd');
            input.startTime = newTime;
            const startD = buildDateFromHHMM(selectedDate, newTime)!;
            const ps = buildDateFromHHMM(selectedDate, prevStart);
            const pe = buildDateFromHHMM(selectedDate, prevEnd);
            if (ps && pe) {
              let durationMins = (pe.getTime() - ps.getTime()) / 60000;
              if (durationMins <= 0) {
                // Crosses midnight
                durationMins += 24 * 60;
              }
              const endD = addMinutes(startD, durationMins);
              input.endTime = format(endD, 'HH:mm');
              // If end spills into next day, set endDate explicitly so backend knows day boundary
              if (endD.getDate() !== startD.getDate()) {
                input.endDate = format(addDays(selectedDate, 1), 'yyyy-MM-dd');
              }
            }
          } else if (prevStart && !prevEnd) {
            input.startDate = format(selectedDate, 'yyyy-MM-dd');
            input.startTime = newTime;
          } else if (!prevStart && prevEnd) {
            input.endDate = format(selectedDate, 'yyyy-MM-dd');
            input.endTime = newTime;
          } else {
            // No times previously → set start only with explicit date so backend persists time fields
            input.startDate = format(selectedDate, 'yyyy-MM-dd');
            input.startTime = newTime;
          }
        } catch (err) {
          console.error(
            'DayView: failed to build input for same-day drop:',
            err
          );
        }
        // For Offered or Accepted-by-other same-day changes, require dialog confirmation
        const st = details.jobStatus;
        const requiresDialog =
          st === JobStatus.Offered ||
          (st === JobStatus.Accepted &&
            !(details.assigneeId && isCurrentUser(details.assigneeId)));

        if (requiresDialog) {
          setRescheduleDetails(details);
          setPendingSameDayInput(input);
          setDialogIsOpen(true);
          return;
        }

        // Fire mutation; immediate path when no dialog required
        rescheduleJobs({ input })
          .then((result) => {
            if (result?.error) {
              console.warn('[DayView] rescheduleJobs error', result.error);
              try {
                toast.error(result.error.message.replace('[GraphQL] ', ''));
              } catch (err) {
                console.error('DayView: failed to show error toast:', err);
              }

              // Revert optimistic change on error
              setLocalCalendar((prev) =>
                prev.map((job) =>
                  job.id === dragItem.eventId
                    ? {
                        ...job,
                        scheduledStartTime: prevTimes.start,
                        scheduledEndTime: prevTimes.end ?? undefined,
                      }
                    : job
                )
              );
            } else {
              try {
                const updated = (result?.data as any)?.rescheduleJobs?.[0];
                if (!updated) return;
                // Prefer scheduleStart when strict; fallback to startTime if backend ever returns it
                const updatedStrict: boolean = strictFlagIsTrue(
                  updated?.scheduledStartStrict as any
                );
                const newStart =
                  updatedStrict && updated?.scheduleStart
                    ? format(parseISO(updated.scheduleStart), 'HH:mm')
                    : updated?.scheduledStartTime ?? null;
                setLocalCalendar((prev) =>
                  prev.map((job) =>
                    job.id === dragItem.eventId
                      ? {
                          ...job,
                          scheduledStartTime: newStart,
                          scheduledStartStrict: updated?.scheduledStartStrict,
                          scheduledStartDate:
                            updated?.scheduledStartDate ??
                            job.scheduledStartDate,
                        }
                      : job
                  )
                );

                // Fire Usetiful completion trigger only for onboarding jobs and only on first scheduling
                try {
                  const raw = window.localStorage.getItem('onboardingJobs');
                  const onboardingIds: string[] = raw ? JSON.parse(raw) : [];
                  const isOnboardingJob =
                    Array.isArray(onboardingIds) &&
                    onboardingIds.includes(dragItem.eventId as string);
                  if (isOnboardingJob && hasNoTimes) {
                    window.dispatchEvent(
                      new CustomEvent('pf:job_scheduled', {
                        detail: { jobId: dragItem.eventId },
                      })
                    );

                    // Re-read onboardingJobs just before writing to avoid clobbering
                    // any concurrent updates from other tabs or code paths.
                    const current =
                      window.localStorage.getItem('onboardingJobs');
                    const currentIds: string[] = current
                      ? JSON.parse(current)
                      : [];
                    if (
                      Array.isArray(currentIds) &&
                      currentIds.includes(dragItem.eventId as string)
                    ) {
                      const remaining = currentIds.filter(
                        (id) => id !== dragItem.eventId
                      );
                      window.localStorage.setItem(
                        'onboardingJobs',
                        JSON.stringify(remaining)
                      );
                    }
                  }
                } catch (err) {
                  console.error(
                    'DayView: failed to clean up onboarding job:',
                    err
                  );
                }
              } catch (err) {
                console.error(
                  'DayView: failed to update localCalendar after reschedule:',
                  err
                );
              }
            }
          })
          .catch((e) => {
            console.warn('[DayView] rescheduleJobs exception', e);
            try {
              toast.error('Failed to save time. Please try again.');
            } catch (err) {
              console.error('DayView: failed to show error toast:', err);
            }
            // Revert optimistic change on exception
            setLocalCalendar((prev) =>
              prev.map((job) =>
                job.id === dragItem.eventId
                  ? {
                      ...job,
                      scheduledStartTime: prevTimes.start,
                      scheduledEndTime: prevTimes.end ?? undefined,
                    }
                  : job
              )
            );
          })
          .finally(() => {
            // Cleanup placeholder - no action needed
          });
        return;
      }
      handleReschedule(details);
    },
    [
      selectedDate,
      placeData?.id,
      localCalendar,
      handleReschedule,
      isCurrentUser,
      rescheduleJobs,
    ]
  );

  // Handle back navigation
  const handleBackClick = useCallback(() => {
    if (isMonthlyViewContext && placeId) {
      // Return to monthly view
      navigate(`/jobs/calendar/${placeId}`);
    } else if (placeId) {
      // Return to horizontal planner
      navigate('/jobs/planner');
    } else {
      // Fallback
      navigate('/jobs/planner');
    }
  }, [navigate, placeId, isMonthlyViewContext]);

  // Navigate to a specific date, preserving context (monthly vs regular)
  const navigateToDate = useCallback(
    (targetDate: Date) => {
      const dateStr = format(targetDate, 'yyyy-MM-dd');
      if (isMonthlyViewContext && placeId) {
        navigate(`/jobs/calendar/${placeId}/day?date=${dateStr}`);
        return;
      }
      if (placeId) {
        navigate(`/jobs/planner/${placeId}/day?date=${dateStr}`);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      params.set('date', dateStr);
      navigate({ search: `?${params.toString()}` });
    },
    [navigate, isMonthlyViewContext, placeId]
  );

  // Stable computePreviewPlacement to avoid re-creating function in JSX
  const computePreviewPlacement = useCallback(
    (hour: number, minutes: number, lane?: number) => {
      const slotMin = (hour - dayStartHour) * 60 + (minutes || 0);
      const slotEndMin = slotMin + 30;

      type SimpleItem = { start: number; end: number };
      const events: SimpleItem[] = scheduledJobs.map((j) => {
        const pos = calculateJobPosition(
          j,
          dayStartHour,
          dayEndHour,
          selectedDate
        );
        return { start: pos.startMin, end: pos.endMin };
      });

      const overlaps = events
        .map((e, idx) => ({ ...e, idx }))
        .filter((e) => e.start < slotEndMin && e.end > slotMin)
        .sort((a, b) => a.start - b.start);

      if (overlaps.length === 0) return null;

      const MAX_COLS = 3;
      const columnEndTimes: number[] = [];
      const takenCols: number[] = [];

      overlaps.forEach((e) => {
        let placed = false;
        for (let c = 0; c < Math.min(columnEndTimes.length, MAX_COLS); c++) {
          if (e.start >= columnEndTimes[c]) {
            columnEndTimes[c] = e.end;
            takenCols.push(c);
            placed = true;
            break;
          }
        }
        if (!placed) {
          if (columnEndTimes.length < MAX_COLS) {
            columnEndTimes.push(e.end);
            takenCols.push(columnEndTimes.length - 1);
          } else {
            let idx = 0;
            for (let i = 1; i < MAX_COLS; i++) {
              if (columnEndTimes[i] < columnEndTimes[idx]) idx = i;
            }
            columnEndTimes[idx] = e.end;
            takenCols.push(idx);
          }
        }
      });

      const effectiveLane =
        typeof lane === 'number'
          ? lane
          : typeof hoverInfo?.lane === 'number'
          ? (hoverInfo.lane as number)
          : 0;

      const totalCols = Math.max(
        1,
        Math.min(
          MAX_COLS,
          Math.max(columnEndTimes.length + 1, effectiveLane + 1)
        )
      );

      const used = new Set<number>(takenCols);
      used.delete(effectiveLane);

      const colWidth = 100 / totalCols;
      let previewCol = Math.max(0, Math.min(totalCols - 1, effectiveLane));
      while (used.has(previewCol)) previewCol += 1;
      if (previewCol >= totalCols) previewCol = totalCols - 1;

      return {
        leftPercent: previewCol * colWidth,
        widthPercent: colWidth,
      } as const;
    },
    [scheduledJobs, hoverInfo, dayStartHour, dayEndHour, selectedDate]
  );

  // Touch swipe navigation (mobile/tablet)
  const touchStartRef = useRef<{
    x: number;
    y: number;
    time: number;
  } | null>(null);
  const lastTouchRef = useRef<{ x: number; y: number } | null>(null);
  const SWIPE_X_THRESHOLD = 50; // px
  const SWIPE_Y_MAX = 40; // px
  const SWIPE_TIME_MAX = 600; // ms

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
    lastTouchRef.current = { x: t.clientX, y: t.clientY };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    lastTouchRef.current = { x: t.clientX, y: t.clientY };
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartRef.current || !lastTouchRef.current) return;
    if (isDraggingAny) return;
    const dt = Date.now() - touchStartRef.current.time;
    const dx = lastTouchRef.current.x - touchStartRef.current.x;
    const dy = lastTouchRef.current.y - touchStartRef.current.y;
    touchStartRef.current = null;
    lastTouchRef.current = null;
    if (dt > SWIPE_TIME_MAX) return;
    if (Math.abs(dx) < SWIPE_X_THRESHOLD) return;
    if (Math.abs(dy) > SWIPE_Y_MAX) return;
    if (dx > 0) {
      // Swipe right → previous day
      navigateToDate(addDays(selectedDate, -1));
    } else {
      // Swipe left → next day
      navigateToDate(addDays(selectedDate, 1));
    }
  }, [isDraggingAny, navigateToDate, selectedDate]);

  if (!placeData && !isLoading) {
    return (
      <div className='flex h-full flex-col bg-white'>
        <div className='border-b border-gray-200 p-4'>
          <div className='flex items-center justify-between'>
            <button
              onClick={handleBackClick}
              className='flex items-center gap-2 text-gray-600 hover:text-gray-900'
            >
              ← Back
            </button>
            <h1 className='text-lg font-semibold'>Day View</h1>
          </div>
        </div>
        <div className='flex flex-1 items-center justify-center'>
          <div className='text-center text-gray-500'>
            <p>Place not found</p>
          </div>
        </div>
      </div>
    );
  }

  // If still loading and no placeData, render empty container
  if (isLoading && !placeData) {
    return <div className='h-full w-full bg-white' />;
  }

  const placeName = placeData!.name;
  // Exclude bookings from the header job count
  const jobCount =
    scheduledJobs.filter((j) => j.status !== 'Booking').length +
    allDayJobs.filter((j) => j.status !== 'Booking').length +
    unscheduledJobs.filter((j) => j.status !== 'Booking').length;
  const dateDisplay = format(selectedDate, 'EEEE dd MMMM');

  return (
    <>
      <div
        className={classNames(
          'flex h-full flex-col bg-white shadow-lg transition-opacity duration-300',
          isLoading ? 'opacity-0' : 'opacity-100'
        )}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-usetiful='day-view-grid'
      >
        {/* Header */}
        <div className='border-b border-gray-200 p-4'>
          <div className='flex items-center justify-between'>
            <IconButton
              icon={
                isMobile
                  ? (faArrowLeft as IconDefinition)
                  : (faXmark as IconDefinition)
              }
              onClick={handleBackClick}
              className='flex items-center gap-2 text-gray-600 transition-colors hover:text-gray-900'
            />
            <div className='text-center'>
              <h2 className='font-semibold text-secondary lg:hidden'>
                {placeName}
              </h2>
              <div className='flex flex-col items-center lg:flex-row'>
                <h1 className='text-lg font-semibold text-primary'>
                  {dateDisplay}
                </h1>
                <p className='-mb-[1px] text-secondary lg:text-sm'>
                  {jobCount} Jobs
                </p>
              </div>
            </div>
            <div className='w-16' /> {/* Spacer for centering */}
          </div>
        </div>

        {/* Main Content Container */}
        <div className='flex-1 overflow-auto'>
          <div className='border-b'></div>
          {/* All Day (multi-day) Section */}
          {allDayJobs.length > 0 && (
            <div className='grid gap-2 border-b border-gray-200 bg-gray-50 py-4'>
              {allDayJobs
                .filter((job) => job.status === 'Booking')
                .map((job: Job) => {
                  const isCheckIn = isSameDay(
                    parseISO(job.start),
                    selectedDate
                  );
                  const isCheckOut = job.end
                    ? isSameDay(parseISO(job.end as string), selectedDate)
                    : false;

                  return (
                    <div
                      key={job.id}
                      role='button'
                      tabIndex={0}
                      aria-label={`Booking${
                        isCheckIn
                          ? `Check-in ${format(parseISO(job.start), 'h:mm a')}`
                          : isCheckOut
                          ? `Check-out ${format(
                              parseISO(job.end as string),
                              'h:mm a'
                            )}`
                          : ''
                      }`}
                      onClick={() => handleJobClick(job.id, job.status)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleJobClick(job.id, job.status);
                        }
                      }}
                      className='h-8 w-full cursor-pointer select-none items-center justify-center border bg-primary-container px-2 text-white outline-none sm:flex'
                    >
                      <span className='truncate text-sm font-medium'>
                        Booking {format(parseISO(job.start), 'dd MMM')} to{' '}
                        {format(parseISO(job.end as string), 'dd MMM')}
                      </span>
                      {isCheckIn && !isCheckOut && (
                        <span className='bg-blue-100 ml-2 rounded px-1.5 py-0.5 text-xs font-medium'>
                          Check-in: {format(parseISO(job.start), 'h:mm a')}
                        </span>
                      )}
                      {isCheckOut && !isCheckIn && (
                        <span className='bg-blue-100 ml-2 rounded px-1.5 py-0.5 text-xs font-medium'>
                          Check-out:{' '}
                          {format(parseISO(job.end as string), 'h:mm a')}
                        </span>
                      )}
                      {isCheckIn && isCheckOut && (
                        <span className='bg-blue-100 ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium'>
                          Check-in {job.start} · Check-out {job.end}
                        </span>
                      )}
                    </div>
                  );
                })}

              {allDayJobs.some((j) => j.status !== 'Booking') && (
                <div className='px-4'>
                  <h2 className='mb-3 text-sm font-medium text-gray-600'>
                    All Day
                  </h2>
                  <div className='grid gap-2'>
                    {allDayJobs
                      .filter((j) => j.status !== 'Booking')
                      .map((job: Job) => (
                        <AllDayJobPill
                          key={job.id}
                          job={job}
                          siteId={placeData!.id}
                          onClick={() => handleJobClick(job.id, job.status)}
                          onDragBegin={() => setIsDraggingAny(true)}
                          onDragEnd={() => setIsDraggingAny(false)}
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Anytime Today Section */}
          {unscheduledJobs.length > 0 && (
            <div className='border-b border-gray-200 bg-gray-50 p-4'>
              <h2 className='mb-3 text-sm font-medium text-gray-600'>
                Anytime Today
              </h2>
              <div className='grid gap-2'>
                {unscheduledJobs.map((job: Job) => (
                  <UnscheduledJobPill
                    key={job.id}
                    job={job}
                    siteId={placeData!.id}
                    onClick={() => handleJobClick(job.id, job.status)}
                    onDragBegin={() => setIsDraggingAny(true)}
                    onDragEnd={() => setIsDraggingAny(false)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Time Grid Container */}
          <div className='p-4 pl-16'>
            {/* Left padding for time labels */}
            <TimeGrid
              onTimeSlotClick={handleTimeSlotClick}
              onTimeSlotDrop={handleTimeSlotDrop}
              selectedDate={selectedDate}
              siteId={placeData!.id}
              startHour={dayStartHour}
              endHour={dayEndHour}
              isDraggingAny={isDraggingAny}
              onGlobalHoverStart={(hour, minutes, lane) => {
                const slotStartMin =
                  (hour - dayStartHour) * 60 + (minutes || 0);
                const next = {
                  slotStartMin,
                  slotEndMin: slotStartMin + 30,
                  lane,
                } as const;
                setHoverInfo(next as any);
              }}
              onGlobalHoverEnd={() => {
                setHoverInfo(null);
              }}
              computePreviewPlacement={computePreviewPlacement}
            >
              {/* Current time line */}
              <CurrentTimeLine
                selectedDate={selectedDate}
                startHour={dayStartHour}
                endHour={dayEndHour}
              />

              {/* Scheduled job pills with side-by-side layout for overlaps */}
              {(() => {
                return renderedScheduledPills;
              })()}
            </TimeGrid>
          </div>
        </div>
      </div>

      <RescheduleJobDialog
        isOpen={dialogIsOpen}
        onClose={() => {
          // Revert optimistic update when user clicks "Go back"
          if (rescheduleDetails && !pendingSameDayInput) {
            revertOptimistic(rescheduleDetails);
          }
          setDialogIsOpen(false);
          setRescheduleDetails(undefined);
          setIsRescheduling(false);
          setPendingSameDayInput(null);
        }}
        isLoading={isRescheduling}
        onSubmit={(_, { notify, comment }) => {
          // Same-day pending input path
          if (pendingSameDayInput && rescheduleDetails) {
            const enriched = {
              ...pendingSameDayInput,
              status:
                rescheduleDetails.jobStatus === JobStatus.Offered
                  ? JobStatus.Offered
                  : rescheduleDetails.jobStatus === JobStatus.Accepted
                  ? JobStatus.Accepted
                  : undefined,
              notify,
              comment,
            };
            setIsRescheduling(true);
            rescheduleJobs({ input: enriched })
              .then((result) => {
                if (result?.error) {
                  console.warn('[DayView] dialog same-day submit error');
                } else {
                  const updated = (result?.data as any)?.rescheduleJobs?.[0];
                  try {
                    if (
                      strictFlagIsTrue(updated?.scheduledStartStrict as any) &&
                      updated?.scheduleStart
                    ) {
                      const hhmm = format(
                        parseISO(updated.scheduleStart),
                        'HH:mm'
                      );
                      setLocalCalendar((prev) =>
                        prev.map((job) =>
                          job.id === rescheduleDetails.jobId
                            ? { ...job, scheduledStartTime: hhmm }
                            : job
                        )
                      );
                    }
                  } catch {
                    /* empty */
                  }
                  setDialogIsOpen(false);
                  setRescheduleDetails(undefined);
                  setPendingSameDayInput(null);
                }
              })
              .finally(() => setIsRescheduling(false));
            return;
          }

          if (!rescheduleDetails) return;
          setIsRescheduling(true);
          rescheduleJob({
            input: {
              id: rescheduleDetails.jobId,
              offsetDays: rescheduleDetails.offsetDays,
              comment: comment || undefined,
              // notify: notify || undefined,
            },
          })
            .then((result) => {
              if (result.error) {
                console.warn('[DayView] dialog submit error → revert');
                revertOptimistic(rescheduleDetails);
              } else {
                setDialogIsOpen(false);
                setRescheduleDetails(undefined);
              }
            })
            .catch(() => {
              revertOptimistic(rescheduleDetails);
            })
            .finally(() => setIsRescheduling(false));
        }}
        jobName={rescheduleDetails?.jobName ?? ''}
        assigneeName={rescheduleDetails?.assigneeName ?? ''}
        action={
          rescheduleDetails?.jobStatus === JobStatus.Offered
            ? 'offer'
            : 'confirm'
        }
        originalDate={rescheduleDetails?.originalDate}
        targetDate={rescheduleDetails?.targetDate}
      />
    </>
  );
};

// DayView component that relies on parent DndProvider context
// Parent routes (PlannerView and CalendarView) provide the necessary DndProvider
export const DayView: React.FC = () => {
  return <DayViewImpl />;
};

export default DayView;
